import React, { useEffect, useState, Fragment, useRef } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage/StandardPage'
import { Filter } from '../../components/_WMS/FilterComponent'
import { FilterExtraAction } from '../../components/_WMS/FilterComponent/types'
import { defaultFilters } from './config'
import { useApi } from '../../hooks/useApi'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { ApiResponse } from '../../typings/ApiResponse'
import { InventoryCheck } from './entities/InventoryCheck'
import { InventoryCheckSearch } from './InventoryCheckSearch'
import { deleteInventoryCheck, getInventoryCheck, updatePriority } from './api'
import { usePagination } from '../../hooks/usePagination'
import { useModal } from '../../hooks/useModal'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { Modal } from '../../components/_EOS/Modal'
import { ChangeInventoryCheckTagComponent } from './modals/ChangeInventoryCheckTagComponent'
import { Status } from '../../hooks/useFetchState'
import { generateTableHeadersInventoryCheck } from '.'
import { InventoryCheckResultModal } from './modals/InventoryCheckResultModal'
import { IconButton, Tooltip, Typography } from '@mui/material'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  SupervisedUserCircle as ReassignUserIcon,
  Flag as FlagIcon
} from '@mui/icons-material'
import { eInventoryCheckState } from '../../enums/eInventoryCheck'
import { EditInventoryCheckModal, EditInventoryCheckModalRef } from './modals/EditInventoryCheckModal'
import { ViewInventoryCheckModal } from './modals/ViewInventoryCheckModal'
import { ReassignInventoryCheckModal } from './modals/ReassignInventoryCheckModal'
import { cfgRoleButton } from '../../configurationButton'
import { validatePermittedRoleButton } from '../../utils/validateRoleHelper'
import { CreateInventoryCheckModal, CreateInventoryCheckModalRef } from './modals/CreateInventoryCheckModal'
import VisibilityIcon from '@mui/icons-material/Visibility'

const PositionAlert = 11

interface InventoryCheckPageProps {}

export const InventoryCheckPage: React.FC<InventoryCheckPageProps> = () => {
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const { state, callApi } = useApi<ApiResponse<InventoryCheck>>()

  const [isPriorizing, setIsPriorizing] = React.useState<boolean>(false)
  const [selectedElement, setSelectedElement] = React.useState<number[]>([])
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)
  const pagination = usePagination(5)

  const [orderBy, setOrderBy] = useState<GenericOrderBy<InventoryCheck>>({
    field: '',
    direction: 'desc'
  })

  const tableHeadersInventoryCheck = generateTableHeadersInventoryCheck()

  const inventoryCheckTagModal = useModal(`Modificar TAG`)

  const handleCheckChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedElement((prev) => [...prev, id])
    } else {
      setSelectedElement((prev) => [...prev.filter((el) => el !== id)])
    }
  }

  const handleCheckAllToggle = (selectAll: boolean) => {
    if (selectAll) {
      // Selecciona todos los IDs de las filas actualmente visibles
      const newCheckedElements =
        state.data?.items.map((item) => item.inventoryCheckId) || []
      setSelectedElement(newCheckedElements)
    } else {
      // Limpia la selección
      setSelectedElement([])
    }
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setSelectedElement([])

    requestInventoryCheck(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<InventoryCheck>) => {
    const auxField = field === 'inventoryCheckId' ? 'inventoryCheckId' : field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  const requestInventoryCheck = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getInventoryCheck(filters, page, pageSize, orderBy)).then(() =>
      setSelectedElement([])
    )
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    requestInventoryCheck(selectedFilters, 0, pagination.pageSize)
  }, [orderBy])

  const requestDelete = (id: number) => {
    callApi(deleteInventoryCheck(id))
      .then(() => {
        openSnackBar(`Registro ha sido eliminado correctamente`, true)
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
      .finally(() => {
        requestInventoryCheck(
          selectedFilters,
          pagination.page,
          pagination.pageSize
        )
        deleteModalState.closeModal()
      })
  }

  const requestUpdatePriority = (
    id: number,
    text: string,
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(updatePriority(id, text, filters, page, pageSize, orderBy))
      .then(() => {
        openSnackBar(`Se repriorizó correctamente la orden ${id}`, true)
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
      .finally(() => {
        requestInventoryCheck(
          selectedFilters,
          pagination.page,
          pagination.pageSize
        )
      })
  }

  const requestChangePriority = (inventoryCheckId: number, text: string) => {
    requestUpdatePriority(
      inventoryCheckId,
      text,
      selectedFilters,
      pagination.page,
      pagination.pageSize
    )
  }

  type CustomActionsProps = {
    id: number
    row: InventoryCheck
  }

  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    return (
      <Fragment>
        <Tooltip title='Detalle' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              viewModalState.openModal(
                'md',
                { id: row.inventoryCheckId },
                'Ver Detalle'
              )
            }
            style={{ verticalAlign: 'bottom' }}
          >
            <AssignmentIcon />
          </IconButton>
        </Tooltip>

        {validatePermittedRoleButton(cfgRoleButton.btnInventoryCheck_Edit) && (
          <Tooltip title='Editar Orden' aria-label='edit'>
            <IconButton
              aria-label='seeBinacle'
              onClick={() =>
                editModalState.openModal('md', { entity: row }, 'Editar Orden')
              }
              style={{ verticalAlign: 'bottom' }}
              disabled={
                row.stateId !== eInventoryCheckState.Creada &&
                row.stateId !== eInventoryCheckState['En Proceso']
              }
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        )}

        {validatePermittedRoleButton(
          cfgRoleButton.btnInventoryCheck_Delete
        ) && (
          <Tooltip title='Eliminar Orden' aria-label='edit'>
            <IconButton
              aria-label='seeBinacle'
              onClick={() =>
                deleteModalState.openModal(
                  'sm',
                  { id: row.inventoryCheckId },
                  'Eliminar Orden'
                )
              }
              style={{ verticalAlign: 'bottom' }}
              disabled={
                row.flowId === PositionAlert ||
                (row.stateId !== eInventoryCheckState.Creada &&
                  row.stateId !== eInventoryCheckState['En Proceso'])
              }
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        )}

        {validatePermittedRoleButton(
          cfgRoleButton.btnInventoryCheck_Reassign
        ) && (
          <Tooltip title='Reasignar Usuarios' aria-label='edit'>
            <IconButton
              aria-label='seeBinacle'
              onClick={() =>
                reassignModalState.openModal(
                  'md',
                  { id: row.inventoryCheckId },
                  'Reasignar Usuarios'
                )
              }
              style={{ verticalAlign: 'bottom' }}
              disabled={
                row.stateId !== eInventoryCheckState.Creada &&
                row.stateId !== eInventoryCheckState['En Proceso']
              }
            >
              <ReassignUserIcon />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title='Resultado' aria-label='edit'>
          <IconButton
            aria-label='seeBinacle'
            onClick={() =>
              resultModalState.openModal(
                'xl',
                { id: row.inventoryCheckId },
                `Resultado ID Cíclico #${row.inventoryCheckId}`
              )
            }
            style={{ verticalAlign: 'bottom' }}
            disabled={row.stateId !== eInventoryCheckState.Finalizada}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  const deleteModalState = useModal<{
    id: number
  }>('')

  const editModalState = useModal<{
    entity: InventoryCheck
  }>('')

  const viewModalState = useModal<{
    id: number
  }>('')

  const reassignModalState = useModal<{
    id: number
  }>('')

  const resultModalState = useModal<{
    id: number
  }>('')

  const createModalState = useModal('Crear Conteo')
  const editModalRef = useRef<EditInventoryCheckModalRef>(null)
  const createModalRef = useRef<CreateInventoryCheckModalRef>(null)
  const [canSubmitCreate, setCanSubmitCreate] = useState<boolean>(false)

  const extraActions: FilterExtraAction[] = [
    {
      label: isPriorizing ? 'Terminar Priorización' : 'Priorizar',
      onClick: () => setIsPriorizing(!isPriorizing),
      icon: <FlagIcon />
    }
  ]

  return (
    <Fragment>
      <StandardPage
        pageTitle=''
      >
        {/* SEARCH */}
        <InventoryCheckSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={requestInventoryCheck}
          onAddButtonClick={() => createModalState.openModal('md')}
          addButtonTitle=''
          createButtonTooltip='Crear Conteo'
          extraActions={extraActions}
          includeSearchOnClean={true}
        />
        {/* TABLE */}
        <GenericTable<InventoryCheck>
          headersConfig={tableHeadersInventoryCheck}
          IDKey={'inventoryCheckId'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          customActionComponent={MyCustomActions}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          showCheckboxes={true}
          onCheckChange={handleCheckChange}
          checkedElements={selectedElement}
          showPriorityMenu={isPriorizing}
          priorization={{
            onTop: (id) => requestChangePriority(id, 'top'),
            onUp: (id) => requestChangePriority(id, 'up'),
            onDown: (id) => requestChangePriority(id, 'down')
          }}
          onSortChange={handleSorting}
          onCheckAll={handleCheckAllToggle}
          orderField={orderBy}
          priorizationNumberKey='rowNum'
        />
      </StandardPage>

      {/* MODALS */}
      <Modal
        open={inventoryCheckTagModal.isOpen}
        title={inventoryCheckTagModal.title}
        maxWidth={inventoryCheckTagModal.size}
        onClose={inventoryCheckTagModal.closeModal}
      >
        <ChangeInventoryCheckTagComponent
          actualTag={''} // Empty, because its massive
          workOrderIds={selectedElement}
          handleCloseModal={inventoryCheckTagModal.closeModal}
          handleSnackbar={openSnackBar}
          requestInventoryCheck={() => {
            requestInventoryCheck(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>
      <Modal
        open={reassignModalState.isOpen}
        title={reassignModalState.title}
        maxWidth={reassignModalState.size}
        onClose={reassignModalState.closeModal}
        cancelButton={{
          action: reassignModalState.closeModal,
          title: 'Cerrar'
        }}
      >
        <ReassignInventoryCheckModal
          handleCloseModal={reassignModalState.closeModal}
          inventoryCheckId={reassignModalState.extendedProps?.id as number}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={viewModalState.isOpen}
        title={viewModalState.title}
        maxWidth={viewModalState.size}
        onClose={viewModalState.closeModal}
        cancelButton={{
          action: viewModalState.closeModal,
          title: 'Cerrar'
        }}
      >
        <ViewInventoryCheckModal
          inventoryCheckId={viewModalState.extendedProps?.id as number}
          handleSnackbar={openSnackBar}
        />
      </Modal>

      <Modal
        open={deleteModalState.isOpen}
        title={deleteModalState.title}
        maxWidth={deleteModalState.size}
        onClose={deleteModalState.closeModal}
        successButton={{
          action: () =>
            requestDelete(deleteModalState.extendedProps?.id as number),
          title: 'Aceptar'
        }}
        cancelButton={{
          action: deleteModalState.closeModal,
          title: 'Cerrar'
        }}
      >
        <Fragment>
          <Typography>
            {' '}
            {`¿Está seguro que desea eliminar la orden N° ${deleteModalState.extendedProps?.id}?`}
          </Typography>
        </Fragment>
      </Modal>

      <Modal
        open={editModalState.isOpen}
        title={editModalState.title}
        maxWidth={editModalState.size}
        onClose={editModalState.closeModal}
        successButton={{
          action: () => editModalRef.current?.submit(),
          title: 'GUARDAR'
        }}
        cancelButton={{
          action: editModalState.closeModal,
          title: 'CERRAR'
        }}
      >
        <EditInventoryCheckModal
          ref={editModalRef}
          inventoryCheck={
            editModalState.extendedProps?.entity as InventoryCheck
          }
          handleSnackbar={openSnackBar}
          handleCloseModal={editModalState.closeModal}
          requestInventoryCheck={() => {
            requestInventoryCheck(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      <Modal
        open={createModalState.isOpen}
        title={createModalState.title}
        maxWidth={createModalState.size}
        onClose={createModalState.closeModal}
        successButton={{
          action: () => createModalRef.current?.submit(),
          title: 'CARGAR',
          disabled: !canSubmitCreate
        }}
        cancelButton={{
          action: createModalState.closeModal,
          title: 'CERRAR'
        }}
      >
        <CreateInventoryCheckModal
          ref={createModalRef}
          onCanSubmitChange={setCanSubmitCreate}
          handleSnackbar={openSnackBar}
          handleCloseModal={createModalState.closeModal}
          requestInventoryCheck={() => {
            requestInventoryCheck(
              selectedFilters,
              pagination.page,
              pagination.pageSize
            )
          }}
        />
      </Modal>

      <Modal
        open={resultModalState.isOpen}
        title={resultModalState.title}
        maxWidth={resultModalState.size}
        onClose={resultModalState.closeModal}
        cancelButton={{
          action: resultModalState.closeModal,
          title: 'Cerrar'
        }}
      >
        <InventoryCheckResultModal
          modalState={resultModalState}
          inventoryCheckId={resultModalState.extendedProps?.id || 0}
        />
      </Modal>

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
